import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Project } from '@/lib/schemas';

export interface SectionConfigMutationArgs {
  project: Project;
  /**
   * The EXACT, complete overrides map to persist. The backend PATCH is a full
   * replace (it stores `{ overrides: <this map> }` verbatim), so the caller is
   * responsible for computing the final map — typically by deriving it from the
   * project's CURRENT overrides via {@link computeNextOverrides}. Whatever is
   * passed here is exactly what ends up stored.
   */
  overrides: Record<string, boolean>;
}

/**
 * Reads the current per-project overrides map off a project object.
 * Returns a fresh object (never the live reference) so callers can mutate safely.
 */
export function readOverrides(
  project: { section_config?: Record<string, unknown> | null } | null | undefined,
): Record<string, boolean> {
  const current =
    ((project?.section_config as Record<string, unknown> | null | undefined)?.overrides as
      | Record<string, boolean>
      | undefined) ?? {};
  return { ...current };
}

/**
 * Override-hygiene core: compute the NEXT overrides map for a single hide/show
 * action, given the CURRENT map and the section's default-stack visibility
 * (Layers 1-4, without per-project overrides).
 *
 * An override should exist ONLY when it differs from the default stack:
 *   - Hide X: if default stack already hides X → CLEAR the key; else set X:false.
 *   - Show X: if default stack already shows X → CLEAR the key; else set X:true.
 *
 * This prevents "lever erosion" — a stale `true`/`false` override that pins a
 * section against all future category-default changes. The result is always
 * derived from `current`, so unrelated keys are preserved untouched.
 */
export function computeNextOverrides(
  current: Record<string, boolean>,
  key: string,
  desiredVisible: boolean,
  defaultStackVisibility: Record<string, boolean>,
): Record<string, boolean> {
  const defaultVisible = defaultStackVisibility[key];

  // Guard: a key absent from the default stack (core section passed by mistake,
  // or an add-on key from a newer deploy) would otherwise ALWAYS write an
  // override (undefined never equals a boolean) — silently pinning it against
  // future category-default changes. No-op instead.
  if (defaultVisible === undefined) {
    console.warn(`computeNextOverrides: unknown section key "${key}" — no-op`);
    return { ...current };
  }

  const next = { ...current };

  if (desiredVisible === defaultVisible) {
    // Default stack already produces the desired state — no override needed.
    delete next[key];
  } else {
    // Default stack disagrees — pin the override to force the desired state.
    next[key] = desiredVisible;
  }

  return next;
}

/**
 * Mutation hook for PATCH /api/projects/{id}/section-config.
 *
 * Full-replace semantics: the backend stores exactly `{ overrides: <args.overrides> }`.
 * The caller passes the complete final map (see {@link computeNextOverrides} /
 * {@link readOverrides}). The hook does NOT merge — passing a partial map here
 * would drop the unmentioned keys.
 */
export function useSectionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ project, overrides }: SectionConfigMutationArgs) => {
      return api.projects.updateSectionConfig(project.id, overrides);
    },
    onSuccess: (data, { project }) => {
      queryClient.setQueryData(['projects', 'detail', project.id], (old: Record<string, unknown> | undefined) => {
        if (!old) return old;
        return { ...old, project: data.project };
      });
      // Invalidate so next fetch gets the freshest section_config
      queryClient.invalidateQueries({ queryKey: ['projects', 'detail', project.id] });
    },
  });
}
